import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Spinner,
  Chip,
  ChipProps,
  SortDescriptor,
  Selection
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  renderCell?: (item: T, index?: number) => React.ReactNode;
}

export interface StatusMapping {
  [key: string]: {
    color: ChipProps["color"];
    label?: string;
  };
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  statusMapping?: StatusMapping;
  statusField?: keyof T;
  isLoading?: boolean;
  emptyContent?: string;
  onRowClick?: (item: T) => void;
  selectionMode?: "none" | "single" | "multiple";
  onSelectionChange?: (keys: Selection) => void;
  selectedKeys?: Selection;
  uniqueKey?: keyof T;
  onSortChange?: (descriptor: SortDescriptor) => void;
  sortDescriptor?: SortDescriptor;
  showPagination?: boolean;
  pageSize?: number;
  className?: string;
  addSerialNumbers?: boolean;
  getRowClassName?: (item: T) => string;
}

// Add custom CSS for action button hover effects
const actionButtonStyles = `
  /* Action button hover effects */
 
`;

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  statusMapping,
  statusField,
  isLoading = false,
  emptyContent,
  onRowClick,
  selectionMode = "none",
  onSelectionChange,
  selectedKeys,
  uniqueKey = "id" as keyof T,
  onSortChange,
  sortDescriptor,
  showPagination = true,
  pageSize = 10,
  className = "",
  addSerialNumbers = false,
  getRowClassName
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<T[]>([]);
  // Keep internal sort descriptor optional to avoid invalid default shape
  const [internalSortDescriptor, setInternalSortDescriptor] = useState<SortDescriptor | undefined>(
    sortDescriptor
  );
  
  // Add serial number column if requested
  const tableColumns = useMemo(() => {
    const result = [...columns];
    
    // Add serial number column if requested and not already present
    if (addSerialNumbers && !result.some(col => col.key === "serialNumber")) {
      result.unshift({
        key: "serialNumber",
        label: "S.No",
        sortable: false, // Serial numbers shouldn't be sortable
        renderCell: (_, index) => {
          if (index === undefined) return "-";
          // Calculate the correct serial number based on current page
          return (currentPage - 1) * pageSize + index + 1;
        }
      });
    }
    
    return result;
  }, [columns, addSerialNumbers, currentPage, pageSize]);
  
  // Compute total pages
  const totalPages = Math.ceil(data.length / pageSize);
  
  // Sort data based on sort descriptor
  const sortedData = useMemo(() => {
    // Use external sort descriptor if provided, otherwise use internal one
    const currentDescriptor = sortDescriptor ?? internalSortDescriptor;
    
    if (!currentDescriptor || !currentDescriptor.column) return [...data];
    
    return [...data].sort((a, b) => {
      const columnKey = currentDescriptor.column as string;
      
      if (a[columnKey] === undefined || b[columnKey] === undefined) {
        return 0;
      }
      
      let aValue = a[columnKey];
      let bValue = b[columnKey];
      
      // Handle different data types
      if (typeof aValue === "string" && typeof bValue === "string") {
        return currentDescriptor.direction === "ascending" 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Try to convert to numbers for numerical comparison
      if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      
      // Compare numbers or other types
      if (currentDescriptor.direction === "ascending") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [data, sortDescriptor, internalSortDescriptor]);
  
  // Update paginated data when sorted data, page, or page size changes
  useEffect(() => {
    if (showPagination) {
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      setPaginatedData(sortedData.slice(start, end));
    } else {
      setPaginatedData(sortedData);
    }
  }, [sortedData, currentPage, pageSize, showPagination]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  // Get status chip color based on status value and mapping
  const getStatusColor = (status: string): ChipProps["color"] => {
    if (!statusMapping || !status) return "default";
    return statusMapping[status]?.color || "default";
  };
  
  // Get the translated status label
  const getStatusLabel = (status: string): string => {
    if (!statusMapping || !status) return t(status);
    return statusMapping[status]?.label ? t(statusMapping[status].label!) : t(status);
  };
  
  // Handler for row clicks
  const handleRowClick = (item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };
  
  // Handle sort change
  const handleSortChange = (descriptor: SortDescriptor) => {
    if (onSortChange) {
      // If external sort handler provided, use it
      onSortChange(descriptor);
    } else {
      // Otherwise, use internal sorting
      setInternalSortDescriptor(descriptor);
    }
    // Always reset to first page when sorting changes
    setCurrentPage(1);
  };
  
  // Add style tag for action button hover effects
  useEffect(() => {
    const styleId = "data-table-action-styles";
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.innerHTML = actionButtonStyles;
      document.head.appendChild(styleElement);
      
      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);
  
  const currentSortDescriptor = sortDescriptor ?? internalSortDescriptor;

  return (
    <div className={`w-full max-w-full overflow-x-auto ${className}`} style={{ width: '100%' }}>
      <Table
        aria-label={t("Data table")}
        isHeaderSticky
        selectionMode={selectionMode}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        {...(currentSortDescriptor ? { sortDescriptor: currentSortDescriptor } : {})}
        onSortChange={handleSortChange}
        className="w-full min-w-full"
        style={{ width: '100%', tableLayout: 'fixed' }}
        bottomContent={
          showPagination && totalPages > 1 ? (
            <div className="flex w-full justify-center">
              <Pagination
                isCompact
                showControls
                showShadow
                color="primary"
                page={currentPage}
                total={totalPages}
                onChange={handlePageChange}
              />
            </div>
          ) : null
        }
      >
        <TableHeader columns={tableColumns}>
          {(column) => (
            <TableColumn 
              key={column.key} 
              allowsSorting={column.sortable}
              className="cursor-pointer"
              style={{ width: column.key === 'actions' ? '120px' : 'auto' }}
            >
              {t(column.label)}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          items={paginatedData}
          isLoading={isLoading}
          loadingContent={<Spinner size="lg" />}
          emptyContent={t(emptyContent || "No data to display")}
        >
          {(item) => (
            <TableRow 
              key={String(item[uniqueKey])} 
              onClick={() => handleRowClick(item)}
              className={`${onRowClick ? "cursor-pointer" : ""} ${getRowClassName ? getRowClassName(item) : ""}`}
            >
              {(columnKey) => {
                const column = tableColumns.find(col => col.key === columnKey);
                // Get index in the current paginated view (important for correct serial numbers)
                const rowIndex = paginatedData.findIndex(row => row[uniqueKey] === item[uniqueKey]);
                
                // If column has custom render function, use it
                if (column?.renderCell) {
                  return (
                    <TableCell>
                      <div className="action-cell">
                        {column.renderCell(item, rowIndex)}
                      </div>
                    </TableCell>
                  );
                }
                
                // Special handling for status field
                if (statusField && columnKey === String(statusField)) {
                  const status = String(item[statusField]);
                  return (
                    <TableCell>
                      <Chip
                        color={getStatusColor(status)}
                        size="sm"
                        variant="flat"
                      >
                        {getStatusLabel(status)}
                      </Chip>
                    </TableCell>
                  );
                }
                
                // Special handling for serialNumber (if not using custom renderer)
                if (columnKey === "serialNumber" && addSerialNumbers) {
                  // Calculate the proper serial number based on the current page and row index
                  const serialNo = rowIndex !== undefined 
                    ? (currentPage - 1) * pageSize + rowIndex + 1 
                    : "-";
                  return <TableCell>{serialNo}</TableCell>;
                }
                
                // Default cell rendering
                return <TableCell className="w-auto">{item[columnKey] !== undefined ? String(item[columnKey]) : '-'}</TableCell>;
              }}
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* Show message when no data and not loading */}
      {data.length === 0 && !isLoading && !emptyContent && (
        <div className="text-center py-8">
          <p className="text-gray-600">
            {t("No data available.")}
          </p>
        </div>
      )}
    </div>
  );
}

export default DataTable; 